import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, Pagination } from '@mui/material';
import { getWeeklyReleaseCalendar, getBasicGameStatuses } from '../services/api';
import GameCard from '../components/GameCard';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Game } from '../types/Game';

const formatWeekRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 〜 ${end.getFullYear()}年${end.getMonth() + 1}月${end.getDate()}日`;
};

interface WeeklyCalendarData {
  results: Game[]; // GameCardSerializer形式のゲームリスト
  d: string; // 週の開始日
  e: string; // 週の終了日
  p: string; // 前週
  n: string; // 次週
}



const WeeklyReleaseCalendarPage = () => {
  const [calendarData, setCalendarData] = useState<WeeklyCalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [gameStatuses, setGameStatuses] = useState<{ [key: number]: number | null }>({});
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const GAMES_PER_PAGE = 30;

  const MIN_DATE = '1970-01-05'; // 1970年の月曜日
  const MAX_DATE = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 730); // 2年先
    return date.toISOString().split('T')[0];
  })();

  const isNavigationDisabled = (date: string, direction: 'prev' | 'next') => {
    if (!date) return true;
    if (direction === 'prev' && date <= MIN_DATE) return true;
    if (direction === 'next' && date >= MAX_DATE) return true;
    return false;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const date = searchParams.get('date');
        const pageParam = searchParams.get('page');
        const currentPage = pageParam ? parseInt(pageParam) : 1;
        setPage(currentPage);
        const data = await getWeeklyReleaseCalendar(date);
        setCalendarData(data);
      } catch (error) {
        console.error('週間発売カレンダーの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  useEffect(() => {
    if (isLoggedIn && calendarData?.results && calendarData.results.length > 0) {
      const gameIds = calendarData.results.map(game => game.game_id);
      getBasicGameStatuses(gameIds).then(statuses => {
        setGameStatuses(statuses);
      });
    }
  }, [calendarData, isLoggedIn]);

  const handleWeekChange = (date: string) => {
    navigate(`/weekly-release-calendar?date=${date}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const currentDate = searchParams.get('date');
    const params = new URLSearchParams(searchParams);
    params.set('page', value.toString());
    navigate(`/weekly-release-calendar?${params.toString()}`);
    window.scrollTo(0, 0);
  };

  const getTotalGames = (data: WeeklyCalendarData) => {
    return data.results?.length || 0;
  };

  const getPaginatedGames = (games: Game[]) => {
    const startIndex = (page - 1) * GAMES_PER_PAGE;
    const endIndex = startIndex + GAMES_PER_PAGE;
    return games.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalGames: number) => {
    return Math.ceil(totalGames / GAMES_PER_PAGE);
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: '1280px', mx: 'auto' }}>
        <LoadingPlaceholder type="card" count={30} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1280px', mx: 'auto' }}>
      <Helmet>
        <title>
          {calendarData && `週間ゲーム発売日カレンダー ${formatWeekRange(calendarData.d, calendarData.e)} | PULSE データベース`}
        </title>
      </Helmet>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          ご利用にあたっての注意事項
        </Typography>
        <Typography variant="body2" component="ul" sx={{ 
          fontSize: { xs: '0.6rem', sm: '0.65rem' },
          lineHeight: 1.2,
          pl: 2,
          m: 0,
          '& li': { mb: 0.5 }
        }}>
          <li>情報はリアルタイムで更新されているわけではないため、実際の発売日と異なる場合があります。</li>
          <li>詳細な発売日が未定の場合、四半期（→3/31など）や年内予定（→12/31）などで暫定的に設定している場合があります。</li>
          <li>正確な発売日情報は、必ず各タイトルの公式サイトやメーカー発表をご確認ください。</li>
        </Typography>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 4,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        gap: 2
      }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.5rem' },
              fontWeight: 600,
              mb: 1
            }}
          >
            {calendarData && `${formatWeekRange(calendarData.d, calendarData.e)}の発売スケジュール`}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontSize: '0.9rem' }}
          >
            {calendarData && `この週は計${getTotalGames(calendarData)}本のゲームが${new Date(calendarData.e) > new Date() ? '発売予定です' : '発売されました'}`}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex',
          gap: 1,
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          '& .MuiButton-root': {
            flex: { xs: 1, sm: 'initial' }
          }
        }}>
          {calendarData && (
            <>
              <Button 
                onClick={() => handleWeekChange(calendarData.p)} 
                startIcon={<ChevronLeft />}
                disabled={isNavigationDisabled(calendarData.p, 'prev')}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none'
                }}
                variant="outlined"
              >
                前週
              </Button>
              <Button 
                onClick={() => handleWeekChange(calendarData.n)}
                endIcon={<ChevronRight />}
                disabled={isNavigationDisabled(calendarData.n, 'next')}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none'
                }}
                variant="outlined"
              >
                次週
              </Button>
            </>
          )}
        </Box>
      </Box>

      {calendarData && calendarData.results?.length > 0 && (
        <>
          <Grid container spacing={{ xs: 1, sm: 1.5 }}>
            {getPaginatedGames(calendarData.results).map((game: Game) => (
              <Grid item xs={4} sm={3} md={2} key={game.game_id}>
                <GameCard 
                  game={game} 
                  initialStatus={isLoggedIn ? gameStatuses[game.game_id] : null}
                />
              </Grid>
            ))}
          </Grid>
          
          {getTotalPages(getTotalGames(calendarData)) > 1 && (
            <Pagination
              count={getTotalPages(getTotalGames(calendarData))}
              page={page}
              onChange={handlePageChange}
              sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}

      {calendarData && (!calendarData.results || calendarData.results.length === 0) && (
        <Box sx={{ 
          textAlign: 'center',
          py: 8,
          color: 'text.secondary'
        }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
            発売予定タイトルはありません
          </Typography>
          <Typography variant="body2">
            この週に発売が予定されているゲームは見つかりませんでした。
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WeeklyReleaseCalendarPage; 